//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { KeyboardEvent, useState } from 'react';
import styled from 'styled-components';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyText, TitleText } from '@components/atoms/text/Text';
import TextInput from '@components/atoms/text-input/TextInput';
import {
  IModalPermissionButtonInfo,
  Modal,
} from '@components/molecules/modal/Modal';
import { KEYS } from '@constants/keyboard';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { useAllLabelsQuery } from '@content-drawer/filters/labels/hooks/useAllLabelsQuery';
import { useUpdateLabelMutation } from '@content-drawer/filters/labels/hooks/useUpdateLabelMutation';
import { LabelInputMatch } from '@content-drawer/filters/labels/utils/LabelInputMatch';
import { validateLabelName } from '@content-drawer/filters/labels/utils/labelsValidator';
import { isDefined } from '@utils/filterPredicates';
import { keyEquals } from '@utils/keyboardUtils';
import { pluralize } from '@utils/stringUtils';

export interface LabelUpdateRequest {
  count: number;
  label: string;
  newLabel: string;
}

export interface LabelUpdateConfirmationDialogProps {
  isOpen: boolean;
  request: LabelUpdateRequest;
  onCanceled: () => void;
  onCompleted: () => void;
  onUpdateLabel: (request: LabelUpdateRequest) => void;
}

export const LabelUpdateConfirmationDialog = ({
  isOpen,
  onCanceled,
  onCompleted,
  onUpdateLabel,
  request,
}: LabelUpdateConfirmationDialogProps) => {
  const [isNameMatching, setIsNameMatching] = useState(false);
  const { count, label, newLabel } = request;
  const [changedLabel, setChangedLabel] = useState(newLabel);
  const [isLabelChanged, setIsLabelChanged] = useState(false);

  const { data: labelObjects, isLoading: areAllLabelsLoading } =
    useAllLabelsQuery();
  const allLabels =
    labelObjects?.data.indexObjects.map((object) =>
      object.userTag.toLowerCase(),
    ) ?? [];

  const { isLoading: isUpdateLabelLoading, mutate } = useUpdateLabelMutation();

  const errorMessage = (() => {
    if (allLabels.includes(newLabel.toLowerCase())) {
      return `The label "${newLabel}" already exists.`;
    }

    if (!validateLabelName(newLabel)) {
      return `Labels can only contain letters, numbers, dashes (-), or underscores
        (_). e.g. flat_lay1`;
    }

    return undefined;
  })();

  const hasError =
    areAllLabelsLoading || isUpdateLabelLoading
      ? false
      : isDefined(errorMessage);

  const updateButton: IModalPermissionButtonInfo = {
    buttonContent: 'Update Label',
    isDisabled:
      (!isNameMatching || isUpdateLabelLoading || hasError) && !isLabelChanged,
    onClick: () => {
      if (isLabelChanged) {
        setIsLabelChanged(false);
        onUpdateLabel({
          label: label,
          newLabel: changedLabel,
          count: count,
        });
      } else {
        onCompleted();
        mutate(request);
      }
    },
    trackingId: ContentDrawerTrackingIds.UPDATE_LABEL_CONFIRMATION,
  };

  const cancelButton: IModalPermissionButtonInfo = {
    buttonContent: 'Cancel',
    onClick: onCanceled,
    trackingId: ContentDrawerTrackingIds.CLOSE_UPDATE_LABEL_CONFIRMATION,
  };

  const handleKeyUp = (e: KeyboardEvent) => {
    if (keyEquals(e, KEYS.ENTER)) {
      setIsLabelChanged(false);
      onUpdateLabel({
        label: label,
        newLabel: changedLabel,
        count: count,
      });
    } else if (keyEquals(e, KEYS.ESC)) {
      onCanceled();
    }
  };

  return (
    <Modal
      closeModal={onCanceled}
      contents={
        <Body>
          <BodyText>
            Are you sure you want to update the label{' '}
            <StyledText>"{label}"</StyledText> to{' '}
            <StyledText>"{newLabel}"</StyledText>? Currently, this label is used
            by {pluralize(count, 'item')}. If the old label is used to filter
            content in Galleries on your website, those experiences may fail.
          </BodyText>
          {!areAllLabelsLoading && !hasError && (
            <LabelInputMatch
              label={label}
              labelAction='update'
              onCancel={onCanceled}
              onComplete={() => {
                onCompleted();
                mutate(request);
              }}
              setIsNameMatching={setIsNameMatching}
            />
          )}
          {!areAllLabelsLoading && hasError && (
            <>
              <TextInput
                autoFocus={true}
                errorText={errorMessage}
                hasError={!isLabelChanged}
                maxLength={50}
                onChange={(value) => {
                  setIsLabelChanged(true);
                  setChangedLabel(value);
                }}
                onKeyUp={handleKeyUp}
                trackingId={ContentDrawerTrackingIds.UPDATE_LABEL}
                value={changedLabel}
              />
            </>
          )}
          {((areAllLabelsLoading && !hasError) || isUpdateLabelLoading) && (
            <CenteredSpinner />
          )}
        </Body>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={
        isLabelChanged || !hasError ? updateButton : cancelButton
      }
      secondaryButtonInfo={cancelButton}
      shouldHideOverlay={true}
      shouldIncludeSecondaryButton={isLabelChanged || !hasError}
      title={<Title>Update label</Title>}
      width='wide'
    />
  );
};

const Body = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const Title = styled(TitleText)(
  ({ theme }) => `
    display: flex;
    flex-wrap: wrap;
    max-width: ${theme.wideMeasure};
  `,
);

const StyledText = styled.span(
  ({ theme }) => `
  display: inline;
  width: fit-content;
  font-weight: ${theme.fontWeights.bold};
`,
);
